import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { Grid } from "@mui/material";
import type { Personne, Mandat } from "models";
import EluCommuneInformations from "./EluCommuneInformations";
import { elusUtil } from "utils";
import Fonctions from "constants/Fonctions";
import { mandatsService } from "services";
import { useErrorHandler, withComponentErrorBoundary } from "utils/errorHandling";
import ElusInformationsSkeleton from "./ElusInformationsSkeleton";

interface ElusCommuneInformationsProps {
  communeId: string;
}

function isPremierAdjoint(mandat: Mandat): boolean {
  return (
    mandat.idFonction === Fonctions.ADJOINT_MAIRE &&
    (mandat?.libelleFonction?.startsWith("1er") ?? false)
  );
}

/**
 * Filtre les mandats que l'on souhaite afficher: Maire, 1er adjoint au maire, délégué à l'AG
 * @param mandat
 * @returns true si le mandat correspond
 */
function importantMandatsFilter(mandat: Mandat): boolean {
  return (
    mandat.idFonction === Fonctions.MAIRE ||
    isPremierAdjoint(mandat) ||
    mandat.idFonction === Fonctions.DELEGUE_ASSEMBLEE_GENERALE
  );
}

function ElusCommuneInformations({ communeId }: Readonly<ElusCommuneInformationsProps>): ReactNode {
  const [importantElus, setImportantElus] = useState<Array<Personne>>([]);

  const { catchErrors, isLoading } = useErrorHandler();

  useEffect(() => {
    async function getMandats(): Promise<void> {
      const _mandats = await mandatsService.getByFilter({
        communesIds: [communeId],
        fonctionsIds: [
          Fonctions.MAIRE,
          Fonctions.ADJOINT_MAIRE,
          Fonctions.DELEGUE_ASSEMBLEE_GENERALE,
        ],
        isActive: true,
      });

      const _importantsMandats = _mandats.filter(importantMandatsFilter);
      const _importantElus = elusUtil
        .getElusFromMandats(_importantsMandats)
        .toSorted(elusUtil.sortElusByMandatImportance);

      setImportantElus(_importantElus);
    }

    void catchErrors(getMandats);
  }, [communeId, catchErrors]);

  if (isLoading) {
    return <ElusInformationsSkeleton />;
  }

  return (
    <Grid container spacing={1.5} size={12} sx={{ alignContent: "flex-start" }}>
      {importantElus.map((p) => (
        <Grid container key={p.id} size={12}>
          <EluCommuneInformations elu={p} />
        </Grid>
      ))}
    </Grid>
  );
}

export const ElusCommuneInformationsWithErrorBoundary =
  withComponentErrorBoundary(ElusCommuneInformations);
